import { useEffect, useState, type ReactNode } from 'react';
import { useMixerModel } from '../../audio/useMixerModel';
import { AuthorText } from '../../components/AuthorText';
import { Credits } from '../../components/Credits';
import { VuMeter } from '../../components/VuMeter';
import { S, faq, t } from '../../i18n/strings';
import { arcadeCase } from '../../lib/text';
import { useActions, useSteve } from '../../state/context';
import { useAudioText, useGuestbookLines } from '../../state/derived';
import { PixelSprite } from './PixelSprite';
import { SteveInvaders } from './SteveInvaders';
import styles from './pixel.module.css';
import { STEVE } from './sprites';

/** Skin 5 — Pixel art : le site comme un jeu 8 bits, mini-jeu compris. */
export function PixelSkin(): ReactNode {
  const { lang, seen, draft, playing, clock } = useSteve();
  const actions = useActions();
  const mixer = useMixerModel();
  const audio = useAudioText();
  const guestbook = useGuestbookLines();
  const questions = faq(lang);
  const [gameOpen, setGameOpen] = useState(false);

  // Le jeu se ferme à `Échap`, comme tout ce qui s'ouvre en premier plan.
  //
  // Pendant la partie, la page ne défile plus (les flèches et l'espace servent
  // à jouer) et le Steve-curseur se retire : il passait au-dessus de l'écran de
  // jeu, où un second poisson orange n'aide personne.
  useEffect(() => {
    if (!gameOpen) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setGameOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.dataset['game'] = 'on';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      delete document.documentElement.dataset['game'];
    };
  }, [gameOpen]);

  return (
    <div className={styles.root}>
      {/* Écran cathodique : au-dessus du contenu, jamais dans le chemin des clics. */}
      <div className={styles.scanlines} aria-hidden="true" />

      <header className={styles.hud}>
        <div className={styles.hudSide}>
          <span className={styles.hudName}>{t(S.pixel.hud.name, lang)}</span>
          <span className={styles.hudLives} aria-hidden="true">
            {t(S.pixel.hud.lives, lang)}
          </span>
          <span>{t(S.pixel.hud.score, lang, { n: String(seen).padStart(6, '0') })}</span>
        </div>
        <div className={styles.hudSide}>
          <span className={styles.hudSignal} role="status">
            {t(playing ? S.pixel.hud.signal : S.pixel.hud.silence, lang)}
          </span>
          <span className={styles.hudClock}>{clock}</span>
        </div>
      </header>

      <div className={styles.column}>
        <section className={styles.hero}>
          <h1 className={styles.title}>
            {t(S.pixel.hero.title_l1, lang)}
            <br />
            {t(S.pixel.hero.title_l2, lang)}
          </h1>

          {/* Le sprite est le bouton START : c'est lui qu'on appuie. */}
          <div className={styles.spriteHolder}>
            <PixelSprite
              sprite={STEVE}
              className={styles.heroSprite}
              label={t(S.pixel.game.label, lang)}
              onClick={() => {
                actions.bump();
                setGameOpen(true);
              }}
            />
          </div>

          <p className={styles.start}>{t(S.pixel.hero.start, lang)}</p>
        </section>

        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>{t(S.pixel.sections.level1, lang)}</h2>
          <p className={styles.paragraph}>{t(S.y2k.who_p1, lang)}</p>
          <p className={styles.paragraph}>
            <AuthorText linkClassName={styles.creditLink}>
              {t(S.paint.canvas.who_p2, lang)}
            </AuthorText>
          </p>
          {/* Le marqueur « appuyez pour continuer » des jeux à dialogue : décoratif. */}
          <div className={styles.more} aria-hidden="true">
            ▼
          </div>
        </section>

        <div className={styles.counters}>
          <section className={styles.counter}>
            <span className={styles.counterValue}>{seen}</span>
            <span className={styles.counterLabel}>{t(S.common.seen_today, lang)}</span>
          </section>
          <section className={styles.counter}>
            <span className={styles.counterValue}>2</span>
            <span className={styles.counterLabel}>{t(S.paint.canvas.legs, lang)}</span>
          </section>
          <section className={styles.counter}>
            <span className={styles.counterValue}>100%</span>
            <span className={styles.counterLabel}>{t(S.common.orange_pct, lang)}</span>
          </section>
        </div>

        <section className={styles.panel}>
          <h2 className={styles.panelTitleSound}>{t(S.pixel.sections.sound_test, lang)}</h2>

          <VuMeter className={styles.vu} barClassName={styles.vuBar} />

          <div className={styles.pads}>
            {mixer.pads.map((pad) => (
              <button key={pad.index} type="button" className={styles.pad} onClick={pad.onHit}>
                <span>{pad.label}</span>
                <span className={styles.padTime}>{pad.timeLabel}</span>
              </button>
            ))}
          </div>

          <div className={styles.buttons}>
            <button type="button" className={styles.button} onClick={actions.toggleAudio}>
              {arcadeCase(audio.label)}
            </button>
            <button type="button" className={styles.button} onClick={mixer.onToggleHold}>
              {arcadeCase(mixer.holdLabel)}
            </button>
            <button type="button" className={styles.button} onClick={mixer.onStop}>
              {arcadeCase(mixer.stopLabel)}
            </button>
          </div>

          {mixer.sliders.map((slider) => (
            <div key={slider.key} className={styles.slider}>
              <label className={styles.sliderLabel} htmlFor={`pixel-${slider.key}`}>
                {arcadeCase(slider.label)}
              </label>
              <input
                id={`pixel-${slider.key}`}
                type="range"
                className={styles.sliderInput}
                min={slider.min}
                max={slider.max}
                step={slider.step}
                value={slider.value}
                onChange={(event) => {
                  slider.onChange(event.currentTarget.valueAsNumber);
                }}
              />
              <output className={styles.sliderValue} htmlFor={`pixel-${slider.key}`}>
                {arcadeCase(slider.valueLabel)}
              </output>
            </div>
          ))}

          <div className={styles.buttons}>
            {mixer.presets.map((preset) => (
              <button
                key={preset.name}
                type="button"
                className={styles.button}
                onClick={preset.onSelect}
              >
                {arcadeCase(preset.label)}
              </button>
            ))}
            <button type="button" className={styles.button} onClick={mixer.onToggleWobble}>
              {arcadeCase(mixer.wobbleLabel)}
            </button>
          </div>

          <p className={styles.audioHint} role="status">
            {audio.hint}
          </p>
        </section>

        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>{t(S.pixel.sections.faq, lang)}</h2>
          <dl className={styles.faq}>
            {questions.map((item) => (
              <div key={item.question}>
                <dt className={styles.question}>▶ {item.question}</dt>
                <dd className={styles.answer}>{item.answer}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className={styles.panel}>
          <h2 className={styles.panelTitleSound}>{t(S.pixel.sections.messages, lang)}</h2>
          <ul className={styles.entries}>
            {guestbook.map((entry) => (
              <li key={entry.id} className={styles.entry}>
                <span className={styles.who}>{entry.who}</span>
                <span className={styles.separator}> : </span>
                <span>{entry.text}</span>
              </li>
            ))}
          </ul>
          <div className={styles.compose}>
            <input
              type="text"
              className={styles.input}
              value={draft}
              placeholder={t(S.guestbook.placeholder, lang)}
              aria-label={t(S.guestbook.placeholder, lang)}
              onChange={(event) => {
                actions.setDraft(event.currentTarget.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') actions.postEntry();
              }}
            />
            <button type="button" className={styles.button} onClick={actions.postEntry}>
              {t(S.pixel.actions.publish, lang)}
            </button>
          </div>
        </section>

        <footer className={styles.credits}>
          <Credits className={styles.creditLine} linkClassName={styles.creditLink} unofficial />
          <span>{t(S.pixel.sections.egg_hint, lang)}</span>
        </footer>
      </div>

      {gameOpen && (
        <SteveInvaders
          onQuit={() => {
            setGameOpen(false);
          }}
        />
      )}
    </div>
  );
}
